<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import { rcaStatusColor, rcaStatusTitle } from '@/enums/quality/rca.status'

import RCATable from '@/views/quality/rca/RcaTable.vue'
import RCADrawer from '@/views/quality/rca/drawer/RcaDrawer.vue'
import Graph from '@/views/quality/rca/Graph.vue'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'

definePage({
  meta: {
    name: 'incident-rca',
    subject: 'quality.menu',
    action: 'investigation_capa',
  },
})

const route = useRoute()
const router = useRouter()

// Determine listing type from route param
const rcaType = computed(() => route.params.type ?? 'incident')
const isKpi = computed(() => rcaType.value === 'kpi')

// ─── Incident RCA listing state ─────────────────────
const refRCATable = ref(null)
const refGraph = ref(null)
const isRCADrawerVisible = ref(false)
const rcaDrawerData = ref()

const showRca = data => {
  rcaDrawerData.value = data
  isRCADrawerVisible.value = true
}

const showRcaResponse = data => {
  rcaDrawerData.value = data
  isRCADrawerVisible.value = true
}

const incidentFilters = ref({})
const { data: filterOptions } = await useApi(createUrl('/incidents/incident-rcas-filters'))

// ─── KPI RCA listing state ──────────────────────────
const searchQuery = ref('')
const debouncedSearch = ref('')
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

let searchTimeout

watch(searchQuery, val => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => { debouncedSearch.value = val }, 500)
})

const kpiHeaders = [
  { title: 'Actions', key: 'actions', sortable: false },
  { title: 'Status', key: 'rca_status' },
  { title: 'KPI Code', key: 'subject.configuration.code' },
  { title: 'KPI Title', key: 'subject.configuration.title' },
  { title: 'Value', key: 'subject.calculated_value' },
  { title: 'Threshold', key: 'threshold', sortable: false },
  { title: 'Period', key: 'period', sortable: false },
  { title: 'Created At', key: 'created_at' },
]

const kpiFilters = computed(() => ({
  ...incidentFilters.value,
  unit_id: cookieRef('selectedUnit'),
  subject_type: 'KPI',
}))

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

const {
  isFetching: kpiFetching,
  data: kpiRcaData,
  execute: fetchKpiRcaList,
} = await useApi(createUrl('/incidents/incident-rcas', {
  query: {
    search: kpiFilters,
    q: debouncedSearch,
    paged: 1,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const kpiRcaListing = computed(() => kpiRcaData.value?.data)
const totalKpiRCA = computed(() => kpiRcaData.value?.meta?.total)

const operatorLabel = op => {
  const map = { GT: '>', LT: '<', GTE: '≥', LTE: '≤', EQ: '=' }

  return map[op] || op
}

// calculated_value is a decimal:4 cast, so it arrives as '6.0000'
const formatKpiValue = value => {
  if (isNullOrUndefined(value) || value === '')
    return 'N/A'

  const number = Number(value)

  return Number.isNaN(number) ? value : number.toFixed(2)
}
</script>

<template>
  <section>
    <!-- Incident RCA Listing -->
    <template v-if="!isKpi">
      <VRow class="match-height mb-4">
        <VCol
          cols="12"
          md="6"
          lg="6"
        >
          <FilterComponent
            v-model:filters="incidentFilters"
            :filters-options="filterOptions"
            :style="{
              cardVariant: 'flat',
              cardClass: 'border',
            }"
          />
        </VCol>
        <VCol
          cols="12"
          md="6"
          lg="6"
        >
          <Graph
            ref="refGraph"
            :filters="incidentFilters"
            subject-type="INCIDENT"
            :style="{
              cardVariant: 'flat',
              cardClass: 'border',
            }"
          />
        </VCol>
      </VRow>

      <RCATable
        ref="refRCATable"
        :filters="incidentFilters"
        subject-type="INCIDENT"
        @show-rca="showRca"
        @show-rca-response="showRcaResponse"
      />

      <RCADrawer
        v-model:isDrawerOpen="isRCADrawerVisible"
        :feedback-data="rcaDrawerData"
      />
    </template>

    <!-- KPI RCA Listing -->
    <template v-else>
      <VRow class="match-height mb-6">
        <VCol
          cols="12"
          md="6"
          lg="6"
        >
          <FilterComponent
            v-model:filters="incidentFilters"
            :filters-options="filterOptions"
            :style="{
              cardVariant: 'flat',
              cardClass: 'border',
            }"
          />
        </VCol>
        <VCol
          cols="12"
          md="6"
          lg="6"
        >
          <Graph
            :filters="incidentFilters"
            subject-type="KPI"
            :style="{
              cardVariant: 'flat',
              cardClass: 'border',
            }"
          />
        </VCol>
      </VRow>

      <VCard
        variant="flat"
        class="border"
      >
        <VCardText class="d-flex flex-column flex-sm-row py-4 gap-4 align-sm-center">
          <div class="ticket-search-field">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>
          <VSpacer />
          <div class="d-flex align-center gap-2">
            <IconBtn
              variant="tonal"
              class="rounded"
              @click="fetchKpiRcaList"
            >
              <VIcon icon="tabler-refresh" />
              <VTooltip
                activator="parent"
                location="top"
              >
                Refresh
              </VTooltip>
            </IconBtn>
          </div>
        </VCardText>

        <VDivider />

        <VDataTableServer
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :items="kpiRcaListing"
          :items-length="totalKpiRCA"
          :headers="kpiHeaders"
          fixed-header
          class="text-no-wrap"
          :loading="kpiFetching"
          @update:options="updateOptions"
        >
          <template #item.rca_status="{ item }">
            <VChip
              :color="rcaStatusColor(item.rca_status)"
              class="text-capitalize font-weight-medium"
              size="small"
            >
              {{ rcaStatusTitle(item.rca_status) }}
            </VChip>
          </template>
          <template #item.subject.calculated_value="{ item }">
            {{ formatKpiValue(item.subject?.calculated_value) }}
          </template>
          <template #item.threshold="{ item }">
            <span v-if="item.subject?.configuration">
              {{ operatorLabel(item.subject.configuration.critical_operator) }}
              {{ item.subject.configuration.critical_value }}
            </span>
          </template>
          <template #item.period="{ item }">
            <span v-if="item.subject">
              {{ formatDate(item.subject.period_start) }} – {{ formatDate(item.subject.period_end) }}
            </span>
          </template>
          <template #item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex justify-start gap-1">
              <IconBtn
                variant="tonal"
                class="rounded"
                size="small"
                @click="router.push({
                  name: 'app-quality-rca-view',
                  params: { type: 'kpi', id: item.id },
                })"
              >
                <VIcon icon="tabler-eye" />
                <VTooltip activator="parent">
                  View
                </VTooltip>
              </IconBtn>
            </div>
          </template>
        </VDataTableServer>
      </VCard>
    </template>
  </section>
</template>

<style>
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>

<style lang="scss" scoped>
.ticket-search-field {
  inline-size: 100%;

  @media (min-width: 600px) {
    inline-size: 20rem;
  }
}
</style>
